// @givmo/social — a renter's composer before any account is live (SMM-WEB5, Jonathan's approved
// walkthrough frame 39): in place of the card, one row per platform this organization may connect
// (the store's offered-providers read, so no dead button), each "Not connected" with Connect, or
// "Needs reconnecting" with Reconnect where its only account lapsed. A connect is the Accounts
// page's own trip (`startConnect`) and comes back to the renter's address. Facebook's and Instagram's
// Connect start the one Meta login, whose picker offers the Page and its linked Instagram account,
// so both rows turn connected together. Someone without connection custody reads the page's words
// instead of a Connect. Once any account is live, the card shows.

import { useState } from 'react';
import { Button, Card, ErrorMessage, testIdProp } from '@givmo/design-system';
import type { Channel } from './composer-model';
import { PlatformMark } from './platform-mark';
import { canManageConnections, type SocialConfig } from './social-config';
import { startConnect, type ConnectStart } from './social-connect';
import { accountWord } from './social-platforms';
import { useOfferedFamilies } from './social-queries';
import { SocialLoading } from './social-ui';
import { socialWords as w } from './social-words';

export function EmbedConnectStep({
  config,
  lapsed,
}: {
  config: SocialConfig;
  /** The organization's accounts that need reconnecting: each offers Reconnect on its row. */
  lapsed: readonly Channel[];
}) {
  const { orgRef } = config;
  const orgName = config.orgName ?? null;
  const manager = canManageConnections(config);
  const offered = useOfferedFamilies(orgRef);
  const [starting, setStarting] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const rows = (offered.data ?? []).flatMap((family) =>
    family.providers.map((provider) => ({ provider, family })),
  );

  const begin = (key: string, start: ConnectStart) => {
    if (orgRef === null) return;
    setStarting(key);
    setRefusal(null);
    // No return address: the trip comes back to the page the person is on, the renter's tab.
    void startConnect(orgRef, start).then((words) => {
      if (words) {
        setStarting(null);
        setRefusal(words);
      }
    });
  };

  return (
    <Card className="flex flex-col gap-0 p-0" testId="social-connect-step">
      <div className="flex flex-col gap-4 border-b border-border-subtle px-20 py-16">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {w.connectStepTitle(orgName)}
        </h2>
        <p className="font-sans text-s14 tracking-givmo text-text-secondary">
          {w.connectStepLine(orgName)}
        </p>
      </div>
      {offered.isPending ? (
        <div className="px-20 py-16">
          <SocialLoading>{w.loadingAccounts}</SocialLoading>
        </div>
      ) : rows.length === 0 ? (
        <p
          className="px-20 py-16 font-sans text-s14 tracking-givmo text-text-secondary"
          {...testIdProp('social-connect-step-none')}
        >
          {w.noneOffered}
        </p>
      ) : (
        <ul className="flex flex-col">
          {rows.map(({ provider, family }) => {
            const lapse = lapsed.find((channel) => channel.platform === provider) ?? null;
            const key = lapse ? lapse.connectionId : family.family;
            const start: ConnectStart = lapse
              ? {
                  kind: 'reconnect',
                  connectionId: lapse.connectionId,
                  provider,
                  family: family.family,
                }
              : { kind: 'connect', family: family.family, providers: family.providers };
            return (
              <li
                key={provider}
                className="flex items-center gap-14 border-b border-border-subtle px-20 py-14 last:border-b-0"
                {...testIdProp(`social-connect-${provider}`)}
              >
                <PlatformMark platform={provider} size={36} />
                <span className="flex min-w-0 flex-col gap-2">
                  <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                    {accountWord(provider)}
                  </span>
                  <span className="font-sans text-s13 tracking-givmo text-text-secondary">
                    {lapse ? w.needsReconnect : w.notConnected}
                  </span>
                </span>
                {manager ? (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    className="ml-auto"
                    loading={starting === key}
                    disabled={starting !== null}
                    onClick={() => begin(key, start)}
                    testId={`social-connect-${provider}-start`}
                  >
                    {lapse ? w.reconnect : w.connect}
                  </Button>
                ) : (
                  <span className="ml-auto text-right font-sans text-s13 tracking-givmo text-text-secondary">
                    {lapse ? w.askAdminToReconnect : w.askAdminToConnect}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {refusal ? (
        <div className="border-t border-border-subtle px-20 py-12">
          <ErrorMessage testId="social-connect-step-error">{refusal}</ErrorMessage>
        </div>
      ) : null}
    </Card>
  );
}
